import { CopyButton } from '@/components/ui/CopyButton'
import { cn } from '@/lib/utils'
import { Cap, Demo, DsSection } from '../primitives'

const BRAND_RAMP = [
  { step: '50', hex: '#F9FCF3', cls: 'bg-brand-50 text-ink', dark: false },
  { step: '100', hex: '#F3F8E2', cls: 'bg-brand-100 text-ink', dark: false },
  { step: '200', hex: '#E8F4C3', cls: 'bg-brand-200 text-ink', dark: false },
  { step: '300', hex: '#DAF098', cls: 'bg-brand-300 text-ink', dark: false },
  { step: '400', hex: '#CFF06A', cls: 'bg-brand-400 text-ink', dark: false },
  { step: '500', hex: '#C6F538', cls: 'bg-brand-500 text-ink', dark: false },
  { step: '600', hex: '#AADA1B', cls: 'bg-brand-600 text-ink', dark: false },
  { step: '700', hex: '#84A71B', cls: 'bg-brand-700 text-white', dark: true },
  { step: '800', hex: '#607817', cls: 'bg-brand-800 text-white', dark: true },
  { step: '900', hex: '#435313', cls: 'bg-brand-900 text-white', dark: true },
]

const INK_RAMP = [
  { step: '25', hex: '#F5F5F3', cls: 'bg-ink-25 text-ink', dark: false },
  { step: '50', hex: '#F0EFEC', cls: 'bg-ink-50 text-ink', dark: false },
  { step: '100', hex: '#E4E3E0', cls: 'bg-ink-100 text-ink', dark: false },
  { step: '200', hex: '#D6D5D2', cls: 'bg-ink-200 text-ink', dark: false },
  { step: '300', hex: '#A7A7A5', cls: 'bg-ink-300 text-ink', dark: false },
  { step: '400', hex: '#848482', cls: 'bg-ink-400 text-white', dark: true },
  { step: '500', hex: '#666764', cls: 'bg-ink-500 text-white', dark: true },
  { step: '600', hex: '#4D4F4C', cls: 'bg-ink-600 text-white', dark: true },
  { step: '700', hex: '#353735', cls: 'bg-ink-700 text-white', dark: true },
  { step: '800', hex: '#272927', cls: 'bg-ink-800 text-white', dark: true },
  { step: '850', hex: '#212321', cls: 'bg-ink-850 text-white', dark: true },
  { step: '900', hex: '#1B1D1B', cls: 'bg-ink-900 text-white', dark: true },
]

function HexCopy({ hex, dark }: { hex: string; dark: boolean }) {
  return (
    <div className="flex items-center gap-0.5">
      <span className="truncate">{hex}</span>
      <CopyButton
        value={hex}
        className={cn(
          'size-4 bg-transparent hover:bg-transparent',
          dark ? 'text-white/70 hover:text-white' : 'text-ink/60 hover:text-ink'
        )}
      />
    </div>
  )
}

function Ramp({ steps }: { steps: typeof BRAND_RAMP }) {
  return (
    <div className="border-ink-100 flex overflow-hidden rounded-sm border">
      {steps.map(({ step, hex, cls, dark }) => (
        <div
          key={step}
          className={`flex h-20 flex-1 flex-col justify-between p-1.5 font-mono text-[9px] ${cls}`}
        >
          <span>{step}</span>
          <HexCopy hex={hex} dark={dark} />
        </div>
      ))}
    </div>
  )
}

function RampLabels({ left, mid, right }: { left: string; mid: string; right: string }) {
  return (
    <div className="text-ink-400 mt-2 flex justify-between font-mono text-[9px]">
      <span>{left}</span>
      <span>{mid}</span>
      <span>{right}</span>
    </div>
  )
}

const STATUS = [
  {
    name: 'Success',
    cls: 'bg-accent-green',
    token: '--green',
    hex: '#31AB6A',
    use: 'healthy / complete / live',
  },
  { name: 'Warning', cls: 'bg-(--amber)', token: '--amber', hex: '#F6B100', use: 'needs attention' },
  { name: 'Danger', cls: 'bg-error', token: '--red', hex: '#E0383B', use: 'error / alert' },
]

const ABSOLUTES = [
  { cls: 'bg-ink', token: '--ink', hex: '#000000' },
  { cls: 'bg-ink-900', token: '--ink-900', hex: '#1B1D1B' },
  { cls: 'bg-ink-50', token: '--ink-50', hex: '#F0EFEC' },
  { cls: 'border border-ink-100 bg-surface', token: '--paper', hex: '#FFFFFF' },
]

function TokenHex({ token, hex }: { token: string; hex: string }) {
  return (
    <div className="mt-2 flex items-center gap-1">
      <Cap>{token}</Cap>
      <span className="text-ink-400 font-mono text-[10px]">{hex}</span>
      <CopyButton
        value={hex}
        className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
      />
    </div>
  )
}

export function ColorSection() {
  return (
    <DsSection id="color" title="Color" marketing>
      <div>
        <Cap>Brand — safety green · 50–900 + DEFAULT(500)</Cap>
        <div className="border-ink-100 mt-3 flex items-center gap-4 rounded-sm border p-4">
          <div className="bg-brand-500 size-16 shrink-0 rounded-sm" />
          <div>
            <div className="text-ink text-sm font-bold">Our brand green</div>
            <div className="text-ink-500 mt-0.5 flex items-center gap-1 font-mono text-xs">
              <span>--brand / brand-500 · #C6F538</span>
              <CopyButton
                value="#C6F538"
                className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
              />
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Ramp steps={BRAND_RAMP} />
          <RampLabels left="--brand-50" mid="--brand = 500" right="--brand-900" />
        </div>
      </div>
      <div>
        <Cap>Ink — warm grays · surfaces, borders, text</Cap>
        <div className="mt-3">
          <Ramp steps={INK_RAMP} />
          <RampLabels left="--ink-25" mid="--ink = 500" right="--ink-900" />
        </div>
        <p className="text-ink-400 mt-2 max-w-2xl text-xs leading-relaxed">
          Use <code>ink-*</code> classes for this ramp — not <code>neutral-*</code>. This codebase also
          defines a separate, cooler-toned <code>neutral-*</code> scale for the private app UI (Claims,
          Dashboard, internal tools); it's real and actively used there, just not part of the marketing/funnel
          language documented on this page.
        </p>
      </div>
      <Demo label="Absolutes vs. surfaces — ink/paper for text & controls; ink-900/ink-50 for large fills">
        <div className="flex gap-3.5">
          {ABSOLUTES.map(({ cls, token, hex }) => (
            <div key={token} className="flex-1">
              <div className={`h-13 rounded-sm ${cls}`} />
              <TokenHex token={token} hex={hex} />
            </div>
          ))}
        </div>
      </Demo>
      <Demo label="Status colors — system state, never substitute brand green">
        <div className="flex gap-3.5">
          {STATUS.map(({ name, cls, token, hex, use }) => (
            <div key={name} className="flex-1">
              <div className={`h-14 rounded-sm ${cls}`} />
              <div className="text-ink mt-2 text-[13px] font-bold">{name}</div>
              <TokenHex token={token} hex={hex} />
              <div className="text-ink-500 mt-1 text-xs">{use}</div>
            </div>
          ))}
        </div>
      </Demo>
      <Demo label="Two greens, one rule">
        <div className="flex gap-4">
          <div className="bg-brand-500 flex min-h-24 flex-1 flex-col justify-between rounded-sm p-4.5">
            <Cap className="text-brand-900">Brand · identity & action</Cap>
            <div>
              <div className="text-ink text-lg font-extrabold tracking-tight">Safety green</div>
              <div className="text-brand-900 flex items-center gap-1 font-mono text-[11px]">
                <span>#C6F538 · yellow-leaning lime</span>
                <CopyButton
                  value="#C6F538"
                  className="text-brand-900/70 hover:text-brand-900 size-4 bg-transparent hover:bg-transparent"
                />
              </div>
            </div>
          </div>
          <div className="bg-accent-green flex min-h-24 flex-1 flex-col justify-between rounded-sm p-4.5 text-white">
            <Cap className="text-white/80">Status · system state</Cap>
            <div>
              <div className="text-lg font-extrabold tracking-tight">Success green</div>
              <div className="flex items-center gap-1 font-mono text-[11px] opacity-85">
                <span>#31AB6A · cooler true green</span>
                <CopyButton
                  value="#31AB6A"
                  className="size-4 bg-transparent text-white/85 hover:bg-transparent hover:text-white"
                />
              </div>
            </div>
          </div>
        </div>
      </Demo>
    </DsSection>
  )
}
